import { AxiosInstance } from "axios";

export class ApiService {
  protected buildCleanParams(
    params?: Record<string, any>,
  ): Record<string, any> | undefined {
    return !params
      ? undefined
      : Object.fromEntries(
          Object.entries(params).filter(
            ([, value]) =>
              value !== undefined && value !== null && value !== "",
          ),
        );
  }

  /**
   * Generic GET request using axios client
   */
  protected async get<T>(
    client: AxiosInstance,
    endpoint: string,
    params?: Record<string, any>,
    skipGlobalErrorToast = false,
  ): Promise<T> {
    try {
      // Filter out undefined/null/empty params
      const cleanParams = this.buildCleanParams(params);

      const config: any = { params: cleanParams };
      if (skipGlobalErrorToast) {
        config.__skipGlobalErrorToast = true;
      }

      const response = await client.get<T>(endpoint, config);
      return response.data;
    } catch (error) {
      // Error is already converted to ApiError by interceptor
      throw error;
    }
  }

  /**
   * Generic POST request using axios client
   */
  protected async post<T>(
    client: AxiosInstance,
    endpoint: string,
    data: any,
  ): Promise<T> {
    const response = await client.post<T>(endpoint, data);
    return response.data;
  }

  /**
   * Generic PUT request using axios client
   */
  protected async put<T>(
    client: AxiosInstance,
    endpoint: string,
    data: any,
  ): Promise<T> {
    const response = await client.put<T>(endpoint, data);
    return response.data;
  }

  /**
   * Generic PATCH request using axios client
   */
  protected async patch<T>(
    client: AxiosInstance,
    endpoint: string,
    data: any,
  ): Promise<T> {
    const response = await client.patch<T>(endpoint, data);
    return response.data;
  }

  /**
   * Generic DELETE request using axios client
   */
  protected async delete<T>(
    client: AxiosInstance,
    endpoint: string,
    skipGlobalErrorToast = false,
  ): Promise<T> {
    try {
      const response = await client.delete<T>(endpoint, {
        // Prevent double toasts when the caller will handle errors explicitly
        __skipGlobalErrorToast: skipGlobalErrorToast,
      } as any);

      // Handle 204 No Content
      if (response.status === 204) {
        return { message: "Resource deleted successfully" } as T;
      }

      return response.data;
    } catch (error) {
      // Error is already converted to ApiError by interceptor
      throw error;
    }
  }
}
